// Small shared pieces for the leads / pipeline / jobs / contacts pages: stage
// pills, people avatars, tabs, key–value lists and the proposal toast. Plain
// words, tokens only; gold is reserved for money staff see.
import type { ReactNode } from "react";
import { cn } from "@aiw/ui";
import { STAGE_GROUP } from "../lib/format";
import type { ProposalNote } from "../hooks/slice-a";

/** Stage tone by the platform's group; Stalled/Lost are quiet, never gold. */
export function StagePill({ stage, className }: { stage: string | null | undefined; className?: string }) {
  const g = stage ? STAGE_GROUP[stage] : undefined;
  const tone =
    stage === "Stalled" ? "bg-warning/15 text-warning"
    : stage === "Lost" ? "bg-muted text-muted-foreground"
    : g === "Pipeline" ? "bg-accent text-accent-foreground"
    : g === "Fulfillment" ? "bg-primary text-primary-foreground"
    : g === "Invoicing" ? "bg-rail text-rail-foreground"
    : g === "Completed" ? "bg-success text-success-foreground"
    : "bg-muted text-muted-foreground";
  return <span className={cn("inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-micro font-medium leading-4", tone, className)}>{stage ?? "—"}</span>;
}

export type PillTone = "plain" | "info" | "ok" | "warn" | "bad";
const PILL: Record<PillTone, string> = {
  plain: "bg-muted text-muted-foreground",
  info: "bg-accent text-accent-foreground",
  ok: "bg-success/15 text-success",
  warn: "bg-warning/15 text-warning",
  bad: "bg-destructive/15 text-destructive",
};
export function Pill({ tone = "plain", children, className }: { tone?: PillTone; children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-micro font-medium leading-4", PILL[tone], className)}>{children}</span>;
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center whitespace-nowrap rounded border bg-background px-1.5 text-micro leading-5", className)}>{children}</span>;
}

/** Outcome words as the view returns them; tone only where it helps. */
export function OutcomePill({ outcome }: { outcome: string | null | undefined }) {
  const tone: PillTone = outcome === "won" || outcome === "completed" ? "ok" : outcome === "stalled" ? "warn" : outcome === "contacted" ? "info" : "plain";
  return <Pill tone={tone}>{outcome ?? "unworked"}</Pill>;
}

/** Initials in the rail colour; Jacky and Jessie get their own tints so a row reads at a glance. */
export function Avatar({ name, size = "sm", className }: { name: string | null | undefined; size?: "sm" | "lg"; className?: string }) {
  const n = (name ?? "").trim();
  const initials = n ? n.split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join("").toUpperCase() : "?";
  const tint = n === "Jacky" ? "bg-rail text-rail-foreground" : n === "Jessie" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground";
  return (
    <span className={cn("inline-grid shrink-0 place-items-center rounded-full font-mono", size === "lg" ? "h-12 w-12 text-base" : "h-6 w-6 text-micro", tint, className)} title={n || undefined}>
      {initials}
    </span>
  );
}

/** `quiet`: say nothing rather than "Unassigned" once the work is underway (12 Oct 2026) — crm.v_lead
 * carries no owner column, so for a lead the pill used to read "Unassigned" on every row, even an
 * opportunity already quoted. Only a genuinely new, untouched enquiry is called unassigned. */
export function Turn({ owner, quiet = false }: { owner: string | null | undefined; quiet?: boolean }) {
  if (!owner) return quiet ? null : <Pill tone="warn">Unassigned</Pill>;
  return <span className="inline-flex items-center gap-1.5 text-sm"><Avatar name={owner} />{owner}</span>;
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { id: T; label: string; n?: number }[] }) {
  return (
    <div className="mb-3 flex flex-wrap gap-1 border-b" role="tablist">
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={t.id === value}
          onClick={() => onChange(t.id)}
          className={cn("-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm", t.id === value ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}
        >
          {t.label}
          {t.n != null && <span className={cn("rounded-full px-1.5 font-mono text-micro leading-4", t.id === value ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground")}>{t.n}</span>}
        </button>
      ))}
    </div>
  );
}

/** Segmented control (All / Key / Notes). */
export function Seg<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { id: T; label: string }[] }) {
  return (
    <span className="inline-flex overflow-hidden rounded-md border text-micro" role="group">
      {items.map((t) => (
        <button key={t.id} type="button" onClick={() => onChange(t.id)} className={cn("px-2 py-1", t.id === value ? "bg-rail text-rail-foreground" : "text-muted-foreground hover:bg-accent")}>{t.label}</button>
      ))}
    </span>
  );
}

export function Kv({ rows, className }: { rows: [string, ReactNode][]; className?: string }) {
  return (
    <dl className={cn("grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm", className)}>
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export const selectCls = "h-9 rounded-md border bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring";
export const inputCls = "h-9 w-full rounded-md border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring";

/** The backbone's answer to a proposal, bottom-centre, in words. */
export function ProposalToast({ note, onClose }: { note: ProposalNote | null; onClose: () => void }) {
  if (!note) return null;
  const tone = note.tone === "ok" ? "border-success/40 bg-card text-foreground" : note.tone === "wait" ? "border-rail/40 bg-card text-foreground" : "border-destructive/40 bg-card text-destructive";
  return (
    <div role="status" className={cn("fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-lg border px-4 py-2 text-sm shadow-lg", tone)}>
      <span className={cn("h-2 w-2 shrink-0 rounded-full", note.tone === "ok" ? "bg-success" : note.tone === "wait" ? "bg-rail" : "bg-destructive")} aria-hidden />
      <span>{note.text}</span>
      <button type="button" className="text-micro text-muted-foreground underline-offset-2 hover:underline" onClick={onClose}>Close</button>
    </div>
  );
}

/** "jacky@…" → "Jacky", for the Mine tab and the owner filter. */
export function firstName(email: string | null | undefined): string {
  const s = (email ?? "").split("@")[0] ?? "";
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

export const daysBetween = (iso: string | null | undefined, now = Date.now()) => (iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000)) : null);
